import { memo } from 'react'

import { useSelector } from 'react-redux'

import { Avatar, Box, Flex, useTheme } from '@chakra-ui/react'
import styled from '@emotion/styled'

import { selectFullName } from '@/redux/me/meSlice'
import { selectSideNavOpen } from '@/redux/ui/uiSlice'
import { SIDE_BAR_WIDTH_CLOSED, SIDE_BAR_WIDTH_OPEN } from '@/styles/theme'

// Style constants
const PROFILE_CARD_OFFSET = '16px'
const PADDING_EXPANDED = '10px 12px'
const PADDING_COLLAPSED = '10px 16px'
const BORDER_RADIUS_EXPANDED = '16px'
const BORDER_RADIUS_COLLAPSED = '10px'
const MARGIN = '0 8px 10px 8px'
const MIN_HEIGHT_EXPANDED = '58px'
const MIN_HEIGHT_COLLAPSED = '42px'
const AVATAR_SIZE_EXPANDED = '48px'
const AVATAR_SIZE_COLLAPSED = '32px'
const AVATAR_TEXT_GAP = '10px'
const AVATAR_FONT_SIZE = '14px'
const AVATAR_FONT_WEIGHT = 700
const TEXT_MARGIN_TOP = 1

const StyledProfileCard = styled.div<{
  isExpanded: boolean
  bgColor: string
}>`
  display: flex;
  width: ${(p) =>
    `calc(${
      p.isExpanded ? SIDE_BAR_WIDTH_OPEN : SIDE_BAR_WIDTH_CLOSED
    } - ${PROFILE_CARD_OFFSET})`};
  align-items: center;
  justify-content: ${(p) => (p.isExpanded ? 'flex-start' : 'center')};
  padding: ${(p) => (p.isExpanded ? PADDING_EXPANDED : PADDING_COLLAPSED)};
  background: ${(p) => p.bgColor};
  border-radius: ${(p) =>
    p.isExpanded ? BORDER_RADIUS_EXPANDED : BORDER_RADIUS_COLLAPSED};
  margin: ${MARGIN};
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  min-height: ${(p) =>
    p.isExpanded ? MIN_HEIGHT_EXPANDED : MIN_HEIGHT_COLLAPSED};
`

const StyledAvatar = styled(Avatar)`
  .chakra-avatar__initials {
    font-size: ${AVATAR_FONT_SIZE};
    font-weight: ${AVATAR_FONT_WEIGHT};
  }
`

interface ProfileInfoIProps {
  fullName: string
  textColor: string
}

const ProfileInfo = memo(({ fullName, textColor }: ProfileInfoIProps) => (
  <Box>
    <Box
      color='gray.500'
      fontSize='xs'
      lineHeight='shorter'
      textTransform='uppercase'
    >
      Logged in as
    </Box>
    <Box
      color={textColor}
      fontSize='sm'
      fontWeight='semibold'
      lineHeight='shorter'
      mt={TEXT_MARGIN_TOP}
      noOfLines={1}
    >
      {fullName}
    </Box>
  </Box>
))

ProfileInfo.displayName = 'ProfileInfo'

export const SideBarProfileCard = memo(() => {
  const theme = useTheme()
  const fullName = useSelector(selectFullName)
  const isSideNavOpen = useSelector(selectSideNavOpen)

  const textColor = theme.colors.sidebar.darkText
  const avatarSize = isSideNavOpen
    ? AVATAR_SIZE_EXPANDED
    : AVATAR_SIZE_COLLAPSED

  return (
    <StyledProfileCard
      aria-label={`Open profile menu. Logged in as ${fullName}`}
      bgColor={theme.colors.brand[175]}
      data-testid='sidebar_profile_card'
      isExpanded={isSideNavOpen}
      role='button'
      tabIndex={0}
    >
      <Flex alignItems='center' gap={AVATAR_TEXT_GAP}>
        <StyledAvatar
          bg={theme.colors.sidebar.avatarBg}
          color='white'
          h={avatarSize}
          name={fullName}
          w={avatarSize}
        />
        {isSideNavOpen && (
          <ProfileInfo fullName={fullName} textColor={textColor} />
        )}
      </Flex>
    </StyledProfileCard>
  )
})

SideBarProfileCard.displayName = 'SideBarProfileCard'
